import { useMutation } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import FormModal, {
  FormGroup,
  FormLabel,
  FormInput,
  ErrorMessage,
} from 'components/FormModal';
import Toaster from 'components/Toaster';
import Tooltip from 'components/Tooltip';
import DeleteConfirmationModal from '../shared/DeleteConfirmationModal';

const CREATE_ACCOUNT_PAYOUT_RECIPIENT = gql`
  mutation CreateAccountPayoutRecipient(
    $input: CreateAccountPayoutRecipientInput!
  ) {
    createAccountPayoutRecipient(input: $input) {
      id
      email
      name
      created_at
    }
  }
`;

const UPDATE_ACCOUNT_PAYOUT_RECIPIENT = gql`
  mutation UpdateAccountPayoutRecipient(
    $input: UpdateAccountPayoutRecipientInput!
  ) {
    updateAccountPayoutRecipient(input: $input) {
      id
      email
      name
      created_at
    }
  }
`;

const DELETE_ACCOUNT_PAYOUT_RECIPIENT = gql`
  mutation DeleteAccountPayoutRecipient($id: Int!) {
    deleteAccountPayoutRecipient(id: $id)
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 4px 0 0 0;
`;

const HeaderLeft = styled.div``;

const ItemCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.muted};
`;

const AddButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 10px 20px;
  }
`;

const RecipientsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RecipientCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) =>
      theme.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1'};
    box-shadow: ${({ theme }) =>
      theme.name === 'dark'
        ? '0 4px 12px rgba(0, 0, 0, 0.3)'
        : '0 4px 12px rgba(0, 0, 0, 0.08)'};
  }
`;

const RecipientIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.15 : 0.08)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const RecipientInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const RecipientEmail = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-all;
`;

const RecipientName = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin-top: 4px;
`;

const RecipientActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButtonWrapper = styled.div`
  button {
    width: 36px;
    height: 36px;
    padding: 0;
  }
`;

const EmptyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(255, 255, 255, 0.95)'};
`;

const EmptyRowIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.15 : 0.08)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const EmptyRowTextBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const EmptyRowTitle = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 2px 0;
`;

const EmptyRowSub = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0;
`;

const EmptyRowButtonWrapper = styled.div`
  flex-shrink: 0;

  button {
    width: auto;
    padding: 8px 14px;
    font-size: 13px;
  }
`;

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RecipientsSection = ({
  payoutRecipients = [],
  setFieldValue,
  accountId,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toaster, setToaster] = useState({ isOpen: false });

  const [createRecipient] = useMutation(CREATE_ACCOUNT_PAYOUT_RECIPIENT);
  const [updateRecipient] = useMutation(UPDATE_ACCOUNT_PAYOUT_RECIPIENT);
  const [deleteRecipient] = useMutation(DELETE_ACCOUNT_PAYOUT_RECIPIENT);

  const theme = useTheme();
  const infoIconColor =
    theme?.colors?.textGray?.dark ||
    (theme?.name === 'dark' ? '#e2e8f0' : '#64748b');

  const resetForm = () => {
    setEmail('');
    setName('');
    setErrors({});
  };

  const openAdd = () => {
    setSelected(null);
    resetForm();
    setShowModal(true);
  };

  const openEdit = (recipient) => {
    setSelected(recipient);
    setEmail(recipient.email || '');
    setName(recipient.name || '');
    setErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelected(null);
    resetForm();
  };

  const validate = () => {
    const next = {};
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      next.email = 'Email is required.';
    } else if (!EMAIL_RE.test(trimmedEmail)) {
      next.email = 'Enter a valid email address.';
    } else {
      const dup = payoutRecipients.some(
        (r) =>
          r.id !== (selected ? selected.id : null) &&
          (r.email || '').toLowerCase() === trimmedEmail.toLowerCase()
      );
      if (dup) next.email = 'This email is already on the list.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const showToast = (type, title, message) =>
    setToaster({ isOpen: true, type, title, message });

  const handleSubmit = async () => {
    if (!validate()) return;

    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    setIsSubmitting(true);

    try {
      if (selected) {
        const { data } = await updateRecipient({
          variables: {
            input: {
              id: parseInt(selected.id),
              email: trimmedEmail,
              name: trimmedName || null,
            },
          },
        });
        const updated = data.updateAccountPayoutRecipient;
        setFieldValue(
          'payout_recipients',
          payoutRecipients.map((r) => (r.id === selected.id ? updated : r))
        );
        showToast('success', 'Success!', 'Recipient updated successfully.');
      } else {
        const { data } = await createRecipient({
          variables: {
            input: {
              account_id: parseInt(accountId),
              email: trimmedEmail,
              name: trimmedName || null,
            },
          },
        });
        const created = data.createAccountPayoutRecipient;
        setFieldValue('payout_recipients', [...payoutRecipients, created]);
        showToast('success', 'Success!', 'Recipient added successfully.');
      }
      closeModal();
    } catch (err) {
      showToast(
        'error',
        'Error',
        err.message || 'Failed to save recipient. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDelete = (recipient) => {
    setSelected(recipient);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selected) return;
    setIsDeleting(true);
    try {
      await deleteRecipient({ variables: { id: parseInt(selected.id) } });
      setFieldValue(
        'payout_recipients',
        payoutRecipients.filter((r) => r.id !== selected.id)
      );
      showToast('success', 'Success!', 'Recipient deleted successfully.');
    } catch (err) {
      showToast('error', 'Error', err.message || 'Failed to delete recipient.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setSelected(null);
    }
  };

  return (
    <>
      <SectionHeader>
        <HeaderLeft>
          <SectionTitle>
            Payout Recipients
            <Tooltip
              info
              content="Each week when a payout is executed, these are the people notified via email that a payout has been initiated. The email includes payout details and links to reports."
              iconColor={infoIconColor}
              iconSize="16px"
            />
            {payoutRecipients.length > 0 && (
              <ItemCount>({payoutRecipients.length})</ItemCount>
            )}
          </SectionTitle>
          <SectionSubtitle>
            Email addresses that receive payout notifications and reports for
            this account.
          </SectionSubtitle>
        </HeaderLeft>
        {payoutRecipients.length > 0 && (
          <AddButtonWrapper>
            <ButtonV2
              elevated
              type="button"
              onClick={openAdd}
              icon={<PlusIcon />}
            >
              Add Recipient
            </ButtonV2>
          </AddButtonWrapper>
        )}
      </SectionHeader>

      {payoutRecipients.length === 0 ? (
        <EmptyRow>
          <EmptyRowIcon>
            <MailIcon />
          </EmptyRowIcon>
          <EmptyRowTextBlock>
            <EmptyRowTitle>No payout recipients yet</EmptyRowTitle>
            <EmptyRowSub>
              Add an email to start sending payout notifications for this
              account.
            </EmptyRowSub>
          </EmptyRowTextBlock>
          <EmptyRowButtonWrapper>
            <ButtonV2
              elevated
              type="button"
              onClick={openAdd}
              icon={<PlusIcon />}
            >
              Add Recipient
            </ButtonV2>
          </EmptyRowButtonWrapper>
        </EmptyRow>
      ) : (
        <RecipientsListContainer>
          {payoutRecipients.map((recipient) => (
            <RecipientCard key={recipient.id}>
              <RecipientIcon>
                <MailIcon />
              </RecipientIcon>
              <RecipientInfo>
                <RecipientEmail>{recipient.email}</RecipientEmail>
                {recipient.name && (
                  <RecipientName>{recipient.name}</RecipientName>
                )}
              </RecipientInfo>
              <RecipientActions>
                <ActionButtonWrapper>
                  <ButtonV2
                    variant="icon"
                    type="button"
                    onClick={() => openEdit(recipient)}
                    title="Edit recipient"
                    icon={<EditIcon />}
                  />
                </ActionButtonWrapper>
                <ActionButtonWrapper>
                  <ButtonV2
                    danger
                    variant="icon"
                    type="button"
                    onClick={() => openDelete(recipient)}
                    title="Delete recipient"
                    icon={<TrashIcon />}
                  />
                </ActionButtonWrapper>
              </RecipientActions>
            </RecipientCard>
          ))}
        </RecipientsListContainer>
      )}

      <FormModal
        isOpen={showModal}
        onClose={closeModal}
        title={selected ? 'Edit Recipient' : 'Add Recipient'}
        subtitle="Email address that receives payout notifications for this account."
        submitText={selected ? 'Save Changes' : 'Add Recipient'}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      >
        <FormGroup>
          <FormLabel htmlFor="recipient-email">Email *</FormLabel>
          <FormInput
            id="recipient-email"
            type="email"
            name="email"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
        </FormGroup>
        <FormGroup>
          <FormLabel htmlFor="recipient-name">Name (optional)</FormLabel>
          <FormInput
            id="recipient-name"
            type="text"
            name="name"
            placeholder="e.g. Finance"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FormGroup>
      </FormModal>

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelected(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={selected?.email || ''}
        itemType="recipient"
        isDeleting={isDeleting}
      />

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </>
  );
};

RecipientsSection.propTypes = {
  payoutRecipients: PropTypes.array,
  setFieldValue: PropTypes.func.isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default RecipientsSection;
